import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { structureApi, type PositionDetail, type PositionNode } from "../../api/structure";
import type { ApiError } from "../../api/client";
import { Modal } from "../../components/Modal";
import { PositionTreePicker } from "../../components/PositionTreePicker";
import { useStructureScope } from "../../lib/structureScope";
import { errorBox, positionTitle, translateError } from "./shared";

export function MoveModal({
  position, onClose, onMoved,
}: {
  position: PositionDetail;
  onClose: () => void;
  onMoved: (saved: PositionDetail, oldParentId: string | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  // The first level is outside every branch of a structure scope (sprint 7 §12).
  const { limited } = useStructureScope();
  const [target, setTarget] = useState<PositionNode | null>(null);
  const [toTopLevel, setToTopLevel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disabledIds = useMemo(() => new Set(position.parentId ? [position.parentId] : []), [position.parentId]);
  const alreadyTopLevel = position.parentId === null;
  const sameParent = toTopLevel ? alreadyTopLevel : !!target && target.id === position.parentId;
  const canSubmit = !submitting && !sameParent && (toTopLevel || !!target);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const saved = await structureApi.move(position.id, { parentId: toTopLevel ? null : target!.id });
      await onMoved(saved, position.parentId);
    } catch (err) {
      setError(translateError(t, err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={t("structure.modal.moveTitle", "Premeštanje pozicije")}
      subtitle={t("structure.modal.moveSub", "Izaberite novog roditelja za „{{name}}“ (cela grana ide sa njom).", { name: positionTitle(position) })}
      onClose={onClose}
      width={640}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {!limited && (
          <label className="check-row">
            <input type="checkbox" checked={toTopLevel} onChange={(e) => setToTopLevel(e.target.checked)} />
            {t("structure.modal.toTopLevel", "Na prvi nivo (bez roditelja)")}
          </label>
        )}
        {!toTopLevel && (
          <PositionTreePicker value={target?.id ?? null} onChange={setTarget} excludeId={position.id} disabledIds={disabledIds} scoped />
        )}
        <div className="muted" style={{ fontSize: 12.5 }}>
          {toTopLevel
            ? alreadyTopLevel
              ? t("structure.modal.moveAlreadyTop", "Pozicija je već na prvom nivou.")
              : t("structure.modal.moveToTop", "Pozicija ide na prvi nivo.")
            : target
              ? sameParent
                ? t("structure.modal.moveSameParent", "To je već trenutni roditelj.")
                : t("structure.modal.moveTarget", "Novi roditelj: {{name}}", { name: positionTitle(target) })
              : t("structure.modal.noTarget", "Nije izabran cilj.")}
        </div>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={!canSubmit}>
            {submitting ? t("action.saving", "Čuvanje…") : t("structure.move", "Premesti")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
